import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import html2canvas from "html2canvas";
import {
  uniq,
  max,
  has,
  find,
  map,
  takeRight,
  omit,
  findIndex,
  filter,
  cloneDeep,
  flatMap,
  chain,
  every,
  remove,
  mapValues,
  includes,
  sortBy,
  union,
  keys,
  orderBy,
  forEach,
  isString,
  groupBy,
} from "lodash";
import { CalcOptions, Series, chartData } from "../../store/metrics/state";
import { KpiService } from "../../kpi/kpi.service";
import { school } from "../../store/schools/state";
import { formatDate } from "@angular/common";
import { MzTrendChartComponent } from "../mz-trend-chart/mz-trend-chart.component";
import { StackedColumnExpandedComponent } from "../../charts/stacked-column-expanded/stacked-column-expanded.component";
import { DIST_CHART_LARGE_NUM_BARS } from "../../constants";
import { DIST_CHART_NUM_SHOW_LABELS } from "../../constants";
import { saveAs, getDownloadTileName } from "../../shared/index";
import { DistrictSchools } from "../../shared/interfaces";

@Component({
  selector: "deep-dive-lightbox",
  templateUrl: "./deep-dive-lightbox.component.html",
  styleUrls: ["./deep-dive-lightbox.component.scss"],
})
export class DeepDiveLightboxComponent implements OnInit {
  chartData;
  filteredChartData: Array<Series>;
  filterDropdown;
  selectedFilterVals;
  metricResultsSearchObj: CalcOptions = {
    subscore_content_area: "Overall",
    subscore_keys: [],
    student_metric_filters: [],
    chart_type: "trend",
    select_all: 0,
    currently_enrolled_filter: 0,
  };
  viewType: string = "chart";
  selectedTimePeriods: Array<string>;
  categories: Array<string>;
  min: number;
  max: number;
  loading: boolean = true;
  schools: Array<school>;
  historical_schools: Array<school>;
  subAreaDropdown: Array<string>;
  selectedSubArea: string;
  subAreaOptions;
  selectedSubAreaOptions = [];
  filterByText: string;
  contentText: string;
  compareByDropdown: Array<string>;
  selectedCompareBy: string;
  noComparisonText: string = "- No comparison -";
  demographics;
  gradeLevelAtTheTimeText: string = "Grade Level At The Time";
  currentGradeLevelText: string = "Current Grade Level";
  exportingData: boolean = false;
  selectedChart: string = "trend";
  tooltipDescriptor: string = "";
  distHasLargeDataset: boolean;
  distShowLabels: boolean;
  standard_aggregate_groups;
  filterByCurrentlyEnrolled: boolean;
  filterByCurrentlyEnrolledText: string = "Showing Currently Enrolled Students";

  @ViewChild("MZTrendChart")
  MZTrendChart: MzTrendChartComponent;

  @ViewChild("StackedColumnExpandedComponent")
  StackedColumnExpandedComponent: StackedColumnExpandedComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<DeepDiveLightboxComponent>,
    private kpiService: KpiService,
    public snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    if (
      this.data &&
      this.data.lightbox &&
      this.data.lightbox.charts &&
      this.data.lightbox.charts.size > 1 && // if there's more than one chart
      (this.data.lightbox.selectedChart == "category" ||
        !(
          this.data.lightbox.charts.trend &&
          this.data.lightbox.charts.trend.chartData
        ))
    ) {
      // if we're coming from the stacked bar chart on the tile, or if we're coming from the overall chart and the trend chart is off
      this.selectedChart = "distribution";
      this.metricResultsSearchObj = {
        ...this.metricResultsSearchObj,
        chart_type: "distribution",
      };
    }
    // get schools for user
    this.kpiService.getUserInfo().subscribe(
      (data) => {
        this.schools = data.schools;
        this.schools = sortBy(this.schools, ["officialSchoolName"]);
        this.selectedTimePeriods = this.getSelectedTimePeriodValuesBasedOnPeriodicity();
        this.categories = this.getAllTimePeriodValuesBasedOnPeriodicity();
        this.getDemographicsAndRealTimeCalculatedMetric();
        // get sub areas
        this.getSubAreas();
      },
      (error) => {
        console.error(error);
      }
    );
  }

  close() {
    this.dialogRef.close();
  }

  setCurrentlyEnrolledFilter() {
    // reset filterByCurrentlyEnrolled
    this.filterByCurrentlyEnrolled = false;

    if (
      this.metricResultsSearchObj.compare_by ||
      (this.metricResultsSearchObj.student_metric_filters &&
        this.metricResultsSearchObj.student_metric_filters.length > 0)
    ) {
      // check value in compare_by
      let hasCurrentlyEnrolledCompareByVal =
        this.standard_aggregate_groups[
          this.metricResultsSearchObj.compare_by
        ] &&
        this.standard_aggregate_groups[this.metricResultsSearchObj.compare_by]
          .filterByCurrentlyEnrolled;
      // check values in student_metric_filters ("field" values)
      let hasCurrentlyEnrolledFilterVal = find(
        this.metricResultsSearchObj.student_metric_filters,
        (filter) => {
          return (
            this.standard_aggregate_groups[filter.field] &&
            this.standard_aggregate_groups[filter.field]
              .filterByCurrentlyEnrolled
          );
        }
      );
      this.filterByCurrentlyEnrolled = !!(
        hasCurrentlyEnrolledCompareByVal || hasCurrentlyEnrolledFilterVal
      );
    }
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      currently_enrolled_filter: this.filterByCurrentlyEnrolled ? 1 : 0,
    };
  }

  handleRealtimeMetricCalculation() {
    this.clearAllChartData();
    this.loading = true;
    this.setCurrentlyEnrolledFilter();
    const searchParams = cloneDeep(this.metricResultsSearchObj);
    if (searchParams.select_all === 1) {
      searchParams.subscore_keys = [];
    }

    this.kpiService
      .realtimeMetricCalculation(this.data.lightbox.kpiId, searchParams)
      .subscribe(
        (data) => {
          if (data && data[0]) {
            // Make sure we are processing data for the correct chart type
            let responseChartType =
              data[0].chart_type == "line" ? "trend" : "distribution";
            if (this.selectedChart != responseChartType) {
              return;
            }
          }

          this.loading = false;

          if (data && data[0]) {
            this.chartData = data[0].chart_data;
            this.mapAndOrderChartData();
            this.min = data[0].min;
            this.max = data[0].max;
            this.chartData = this.getVisibleChartData(
              this.selectedTimePeriods,
              true
            );
            //filter out any hidden points initially
            this.filteredChartData = this.customSort(
              this.filterHiddenPoints(cloneDeep(this.chartData))
            );
            this.setTitleText();
            // get tooltip descriptor
            if (this.chartData && this.chartData[0]) {
              this.tooltipDescriptor = this.chartData[0].descriptor;
            }
            this.distHasLargeDataset =
              this.selectedChart == "distribution"
                ? this.hasLargeDataset()
                : false;
            this.distShowLabels =
              this.selectedChart == "distribution"
                ? this.showDistChartLabels()
                : false;
          } else {
            this.clearAllChartData();
          }
        },
        (error) => {
          this.clearAllChartData();
          console.error(error);
        }
      );
  }

  mapAndOrderChartData() {
    if (
      this.chartData &&
      this.chartData[0] &&
      this.chartData[0].compare_by &&
      this.chartData[0].compare_by != "Overall"
    ) {
      if (this.selectedSubArea != "Overall") {
        // map name -> name + ' - ' + compare_by
        // add field "content" -> name
        // map compare_by -> mapped compare_by
        this.chartData = map(this.chartData, (series: Series) => {
          return {
            ...series,
            content: series.name,
            name: series.name + " - " + this.mapCompareBy(series.compare_by),
            compare_by: this.mapCompareBy(series.compare_by),
          };
        });
        if (
          !(
            this.selectedCompareBy == this.gradeLevelAtTheTimeText ||
            this.selectedCompareBy == this.currentGradeLevelText
          )
        ) {
          if (this.selectedSubArea == "Time of Day") {
            this.chartData = orderBy(
              map(this.chartData, (series: Series) => {
                return {
                  ...series,
                  content: series.name,
                  sortNum: this.getSortValueFromTime(series.name),
                };
              }),
              ["sortNum", "content", "compare_by"],
              ["asc", "asc", "asc"]
            );
          } else {
            this.chartData = orderBy(
              this.chartData,
              ["content", "compare_by"],
              ["asc", "asc"]
            );
          }
        } else {
          if (this.selectedSubArea == "Time of Day") {
            this.chartData = orderBy(
              this.chartData,
              [
                (item: Series) => this.getSortValueFromTime(item.name),
                (item: Series) => item.content,
                (item: Series) =>
                  this.demographics && this.demographics[this.selectedCompareBy]
                    ? this.demographics[this.selectedCompareBy].indexOf(
                        item.compare_by
                      )
                    : item.compare_by,
              ],
              ["asc", "asc", "asc"]
            );
          } else {
            this.chartData = orderBy(
              this.chartData,
              [
                (item: Series) => item.content,
                (item: Series) =>
                  this.demographics && this.demographics[this.selectedCompareBy]
                    ? this.demographics[this.selectedCompareBy].indexOf(
                        item.compare_by
                      )
                    : item.compare_by,
              ],
              ["asc", "asc"]
            );
          }
        }
      } else if (this.selectedSubArea == "Overall") {
        // map name -> mapped compare_by
        // map compare_by -> mapped compare_by
        this.chartData = map(this.chartData, (series: Series) => {
          return {
            ...series,
            content: series.name,
            name: this.mapCompareBy(series.compare_by),
            compare_by: this.mapCompareBy(series.compare_by),
          };
        });
        if (this.selectedChart != "distribution") {
          if (
            !(
              this.selectedCompareBy == this.gradeLevelAtTheTimeText ||
              this.selectedCompareBy == this.currentGradeLevelText
            )
          ) {
            this.chartData = orderBy(this.chartData, ["compare_by"], ["asc"]);
          } else {
            this.chartData = orderBy(
              this.chartData,
              [
                (item: Series) =>
                  this.demographics && this.demographics[this.selectedCompareBy]
                    ? this.demographics[this.selectedCompareBy].indexOf(
                        item.compare_by
                      )
                    : item.compare_by,
              ],
              ["asc"]
            );
          }
        }
      }
    } else {
      if (this.selectedSubArea == "Time of Day") {
        // For time of day, sort chronologically (sorting by the calculated sort value)
        this.chartData = orderBy(
          map(this.chartData, (series: Series) => {
            return {
              ...series,
              content: series.name,
              sortNum: this.getSortValueFromTime(series.name),
            };
          }),
          ["sortNum"],
          ["asc"]
        );
      } else {
        // add field "content" -> name for highcharts tooltip
        this.chartData = map(this.chartData, (series: Series) => {
          return { ...series, content: series.name };
        });
        if (this.selectedChart != "distribution") {
          this.chartData = orderBy(this.chartData, ["name"], ["asc"]);
        }
      }
    }
  }

  getSortValueFromTime(name) {
    if (name == "Outside of School Hours") {
      return 98;
    }
    if (name == "Unknown Time") {
      return 99;
    }
    // Name will be in the format of '1-1:59 PM'. To get the sort value, take the initial time number and add 12 if PM.
    // Special case for noon (don't want to sort it after the other PM times)
    let num = parseInt(name.split("-")[0], 10);
    if (name.includes("PM") && num != 12) {
      return num + 12;
    }
    return num;
  }

  setTitleText() {
    this.filterByText =
      this.selectedFilterVals &&
      this.selectedFilterVals.length > 1 &&
      this.selectedFilterVals[0].subfield
        ? `${this.selectedFilterVals[0].subfield.name} and ${
            this.selectedFilterVals.length - 1
          } more`
        : this.selectedFilterVals &&
          this.selectedFilterVals[0] &&
          this.selectedFilterVals[0].subfield
        ? this.selectedFilterVals[0].subfield.name
        : "";
    if (this.subAreaDropdown && this.subAreaDropdown.length > 1) {
      // we have additional sub areas than just 'overall'
      if (
        this.selectedSubAreaOptions &&
        this.selectedSubAreaOptions.length == 1
      ) {
        this.contentText =
          this.selectedSubAreaOptions[0].subfield &&
          this.selectedSubAreaOptions[0].subfield.name
            ? this.selectedSubAreaOptions[0].subfield.name
            : null;
      } else if (
        this.selectedSubAreaOptions &&
        this.selectedSubAreaOptions.length > 1
      ) {
        if (this.selectedSubArea == "Time of Day") {
          this.contentText = "Multiple Times of Day";
        } else if (this.selectedSubArea == "Literary Category") {
          this.contentText = "Multiple Literary Categories";
        } else if (this.selectedSubArea == "Lesson Category"){
          this.contentText = "Multiple Lesson Categories";
        } else {
          this.contentText = `Multiple ${this.selectedSubArea}s`;
        }
      } else if (this.selectedSubArea == "Overall") {
        this.contentText = "Overall";
      } else {
        this.contentText = null;
      }
    } else {
      this.contentText = null;
    }
  }

  clearAllChartData() {
    this.loading = false;
    this.chartData = null;
    this.min = null;
    this.max = null;
    this.filteredChartData = null;
    this.filterByText = null;
    this.contentText = null;
    this.filterByCurrentlyEnrolled = null;
  }

  filterHiddenPoints(fullDataset) {
    let visibleDataset = map(fullDataset, (series: any) => {
      return { ...series, data: filter(series.data, ["visible", true]) };
    });
    // if series' visible data is array(0), then filter series out of dataset
    return filter(visibleDataset, (series: any) => {
      return series.data && series.data.length > 0;
    });
  }

  getSelectedTimePeriodValuesBasedOnPeriodicity() {
    let selectedTimePeriodValues: Array<string>;
    if (this.data.periodicity == "annual") {
      selectedTimePeriodValues = map(
        filter(this.data.lightbox.timePeriodLabels, ["selected", true]),
        "year"
      );
    } else {
      // filter the time periods removing any non-selected labels
      let selectedTimePeriods: any = map(
        this.data.lightbox.timePeriodLabels,
        (timePeriodLabel: any) => {
          return {
            ...timePeriodLabel,
            labels: filter(timePeriodLabel.labels, ["selected", true]),
          };
        }
      );
      // get an array of label values (only the selected ones)
      selectedTimePeriodValues = flatMap(selectedTimePeriods, ({ labels }) =>
        map(labels, (label) => {
          return label["value"];
        })
      );
    }
    return selectedTimePeriodValues;
  }

  getAllTimePeriodValuesBasedOnPeriodicity() {
    let allTimePeriodValues: Array<string>;
    if (this.data.periodicity == "annual") {
      allTimePeriodValues = map(this.data.lightbox.timePeriodLabels, "year");
    } else {
      // Get one array of all time period labels
      allTimePeriodValues = flatMap(
        this.data.lightbox.timePeriodLabels,
        ({ labels }) =>
          map(labels, (label) => {
            return label["value"];
          })
      );
    }
    return allTimePeriodValues;
  }

  getVisibleChartData(selectedTimePeriods, onLoad?: boolean) {
    return map(this.chartData, (series: any) => {
      // use the selected time periods to determine which data points will be visible
      return (series = {
        ...series,
        data: map(series.data, (point: any) => {
          return {
            ...point,
            visible: selectedTimePeriods.indexOf(point.name) > -1,
          };
        }),
        visible: onLoad ? true : series.visible,
      });
    });
  }

  toggleYear(year) {
    this.kpiService
      .trackMetricZoomTimePeriodSelected(this.data.lightbox.kpiId, year)
      .subscribe();
    // update the time period labels' visibility
    this.data.lightbox.timePeriodLabels = map(
      this.data.lightbox.timePeriodLabels,
      (timePeriod: any) => {
        return timePeriod.year == year
          ? {
              ...timePeriod,
              selected: !timePeriod.selected,
              labels: timePeriod.labels
                ? map(timePeriod.labels, (label: any) => {
                    return { ...label, selected: !timePeriod.selected };
                  })
                : null,
            }
          : timePeriod;
      }
    );
    // update the chart data's visibility based on the time period label's visibility
    this.selectedTimePeriods = this.getSelectedTimePeriodValuesBasedOnPeriodicity();
    this.chartData = this.getVisibleChartData(this.selectedTimePeriods);
    // update the filtered data
    this.filteredChartData = this.customSort(
      this.filterHiddenPoints(cloneDeep(this.chartData))
    );
    if (this.viewType == "chart") {
      // filter the chart data based on the visibility for the chart
      this.setNewChartOptionsForTimePeriodToggle();
    }
  }

  toggleTimePeriodLabel(timePeriodLabelEvent) {
    let timePeriodLabelValue = timePeriodLabelEvent.value;
    let year = timePeriodLabelEvent.year;
    this.kpiService
      .trackMetricZoomTimePeriodSelected(
        this.data.lightbox.kpiId,
        timePeriodLabelValue
      )
      .subscribe();
    // update the time period labels' visibility
    let updatedTimePeriod = filter(this.data.lightbox.timePeriodLabels, [
      "year",
      year,
    ])[0];
    updatedTimePeriod = {
      ...updatedTimePeriod,
      labels: map(updatedTimePeriod.labels, (label: any) => {
        return label.value == timePeriodLabelValue
          ? { ...label, selected: !label.selected }
          : label;
      }),
    };
    // now that the label's selectedness is updated, update the overall year's selectedness
    updatedTimePeriod = {
      ...updatedTimePeriod,
      selected: every(updatedTimePeriod.labels, ["selected", true]),
    };
    // update time period labels obj
    this.data.lightbox.timePeriodLabels = map(
      this.data.lightbox.timePeriodLabels,
      (timePeriod: any) => {
        return timePeriod.year == year ? updatedTimePeriod : timePeriod;
      }
    );
    // update the selected time periods
    this.selectedTimePeriods = this.getSelectedTimePeriodValuesBasedOnPeriodicity();
    // go through each series
    // check data for time period label, toggle point
    this.chartData = map(this.chartData, (series: any) => {
      return (series = {
        ...series,
        data: map(series.data, (point: any) => {
          return point.name == timePeriodLabelValue
            ? { ...point, visible: !point.visible }
            : point;
        }),
      });
    });
    // update the filtered data
    this.filteredChartData = this.customSort(
      this.filterHiddenPoints(cloneDeep(this.chartData))
    );
    if (this.viewType == "chart") {
      // filter the chart data based on the visibility for the chart
      this.setNewChartOptionsForTimePeriodToggle();
    }
  }

  setNewChartOptionsForTimePeriodToggle() {
    let chartToUpdate = null;
    let updatedMin = this.min;
    let updatedMax = this.max;
    if (this.selectedChart == "trend" && this.MZTrendChart) {
      chartToUpdate = this.MZTrendChart.chart;
    } else if (this.selectedChart == "distribution") {
      this.distHasLargeDataset = this.hasLargeDataset();
      this.distShowLabels = this.showDistChartLabels();
      chartToUpdate = this.StackedColumnExpandedComponent;
      updatedMin = 0;
      updatedMax = 100;
    }
    if (chartToUpdate) {
      let updatedChartOptions = {
        ...chartToUpdate.options,
        series: this.filteredChartData,
        yAxis: {
          min: Math.floor(updatedMin),
          max: Math.ceil(updatedMax),
        },
      };
      chartToUpdate.updateChartWithNewData(updatedChartOptions);
    }
  }

  getSubAreas() {
    this.subAreaDropdown = ["Overall"];
    if (this.data.lightbox.subAreaLabels) {
      let subAreasWithOptions = filter(
        this.data.lightbox.subAreaLabels,
        (subArea) => {
          return subArea.options && subArea.options.length > 0;
        }
      );
      this.subAreaDropdown = union(
        this.subAreaDropdown,
        map(subAreasWithOptions, "category")
      );
    }
    this.selectedSubArea = this.subAreaDropdown[0];
  }

  getDemographicsAndRealTimeCalculatedMetric() {
    this.kpiService.getDistrictSchools().subscribe(
      (data: DistrictSchools) => {
        if (data) {
          // get standard_aggregate_groups object which has filterByCurrentlyEnrolled flag
          this.standard_aggregate_groups = data.standard_aggregate_groups;

          //Use new historical_demographics list if present.  Fall back to old school_demographics list.
          this.demographics = data.historical_demographics
            ? data.historical_demographics
            : data.school_demographics;

          //Use new historical_schools list if present.  Fall back to old schools list.
          this.historical_schools = data.historical_schools
            ? data.historical_schools
            : data.schools;

          // conditionally setting "School Region" and "School Level" based on whether they have values AND don't set if on School Profile
          let combinedDemogsAndSchoolVals = {
            ...this.demographics,
            ...(data.schoolRegions &&
              data.schoolRegions.length > 0 &&
              !this.data.schoolId && { "School Region": data.schoolRegions }),
            ...(data.schoolLevels &&
              data.schoolLevels.length > 0 &&
              !this.data.schoolId && { "School Level": data.schoolLevels }),
          };
          /* we want filterDropdown in the format of
            this.filterDropdown = [
              {"field": "Gender", "subfield": {"name": "Female", "id": "Female"}},
              {"field": "Gender", "subfield": {"name": "Male", "id": "Male"}},
              {"field": "Schools", "subfield": {"name": "School1", "id": 1}}
            ]
           */
          let demographics = map(
            combinedDemogsAndSchoolVals,
            (vals: Array<string>, key: string) => {
              return map(vals, (val) => {
                return { field: key, subfield: { name: val, id: val } };
              });
            }
          );
          this.filterDropdown = [].concat.apply([], demographics);
          this.filterDropdown = this.filterDropdown.concat(
            map(this.schools, (school: school) => {
              return {
                field: "Schools",
                subfield: {
                  name: school.officialSchoolName,
                  id: school.systemSchoolId,
                },
              };
            })
          );
          this.filterDropdown = sortBy(this.filterDropdown, ["field"]);
          /*
            we want the compareByDropdown in the format of
            this.compareByDropdown = ["- No comparison -", "Gender", "School",...]
          */
          this.compareByDropdown = [
            this.noComparisonText,
            ...sortBy([...keys(combinedDemogsAndSchoolVals), "School"]),
          ];
          // auto-select school if this.data.schoolId is set
          if (this.data.schoolId) {
            this.selectedFilterVals = filter(
              this.filterDropdown,
              (dropdownItem) => {
                return (
                  dropdownItem.field == "Schools" &&
                  dropdownItem.subfield.id == this.data.schoolId
                );
              }
            );
            this.metricResultsSearchObj = {
              ...this.metricResultsSearchObj,
              student_metric_filters: [
                { field: "systemSchoolId", values: [this.data.schoolId] },
              ],
            };
          }
          this.handleRealtimeMetricCalculation();
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  filtersSelected(filters) {
    if (filters.type == "change") {
      // ng-select is sometimes emitting a second "change" event after the original legitimate event
      return filters.preventDefault();
    }
    /*
     * We want the student_metric_filters part of metricResultsSearchObj to have the format
     * {
        "student_metric_filters":[
          {"field":"Gender", "values":["Female"]},
          {"field":"Is ELL", "values":["Is ELL","Former ELL"]}]
       }
     *
     * */
    this.selectedFilterVals = filters;
    filters = groupBy(filters, "field");
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      student_metric_filters: map(
        filters,
        (values: Array<any>, key: string) => {
          return {
            field: key === "Schools" ? "systemSchoolId" : key,
            values: map(map(values, "subfield"), "id"),
          };
        }
      ).filter((obj) => {
        return obj.values && obj.values.length > 0;
      }),
    };

    this.kpiService
      .trackMetricZoomFilterBySelected(
        this.data.lightbox.kpiId,
        this.metricResultsSearchObj.student_metric_filters
      )
      .subscribe();
    this.handleRealtimeMetricCalculation();
  }

  unselectAllFilters() {
    this.selectedFilterVals = [];
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      student_metric_filters: [],
    };
    this.handleRealtimeMetricCalculation();
  }

  clear(item) {
    let field = item.field,
      subfield = item.subfield;
    // remove item from selectedFilterVals and from the metricResultsSearchObj
    this.selectedFilterVals = filter(this.selectedFilterVals, (filterVal) => {
      return !(filterVal.field == field && filterVal.subfield == subfield);
    });
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      student_metric_filters: map(
        this.metricResultsSearchObj.student_metric_filters,
        (metricFilter: any) => {
          if (
            metricFilter.field == field ||
            (field == "Schools" && metricFilter.field == "systemSchoolId")
          ) {
            return {
              ...metricFilter,
              values: filter(metricFilter.values, (filterValue) => {
                return filterValue != subfield.id;
              }),
            };
          }
          return metricFilter;
        }
      ).filter((obj) => {
        return obj.values && obj.values.length > 0;
      }),
    };
    this.handleRealtimeMetricCalculation();
  }

  changeViewType(newtype) {
    this.viewType = newtype.value;
    if (this.viewType == "chart") {
      this.kpiService
        .trackMetricZoomGraphSelected(this.data.lightbox.kpiId)
        .subscribe();
      this.filteredChartData = this.customSort(
        this.filterHiddenPoints(cloneDeep(this.chartData))
      );
      this.setNewChartOptionsForTimePeriodToggle();
    } else {
      this.kpiService
        .trackMetricZoomTableSelected(this.data.lightbox.kpiId)
        .subscribe();
    }
  }

  toggleSubAreaVisibility() {
    this.kpiService
      .trackMetricZoomContentTypeSelected(
        this.data.lightbox.kpiId,
        this.selectedSubArea
      )
      .subscribe();
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      subscore_content_area: this.selectedSubArea,
    };
    if (this.selectedSubArea === "Overall") {
      this.unselectAllSubAreas();
      this.subAreaOptions = [];
    } else {
      let selectedSubAreaLabelObject = find(
        this.data.lightbox.subAreaLabels,
        (subArea) => {
          return subArea.category === this.selectedSubArea;
        }
      );
      let fieldName = "All " + this.selectedSubArea + " Items";
      this.subAreaOptions = selectedSubAreaLabelObject.options.map(
        (subAreaOption: any) => {
          return {
            field: fieldName,
            subfield: {
              id: subAreaOption.id,
              name: subAreaOption.label,
              keys: subAreaOption.keys,
            },
          };
        }
      );
      this.metricResultsSearchObj.select_all = 1;
      this.setSelectedSubAreaOptions(this.subAreaOptions);
    }
  }

  subAreasSelected(filters) {
    if (filters.type === "change") {
      // ng-select is sometimes emitting a second "change" event after the original legitimate event
      return filters.preventDefault();
    }

    let names = flatMap(filters, (subAreaOption: any) => {
      return subAreaOption.subfield.name;
    });
    this.kpiService
      .trackMetricZoomContentSubtypeSelected(this.data.lightbox.kpiId, names)
      .subscribe();
    this.metricResultsSearchObj.select_all =
      filters.length === this.subAreaOptions.length ? 1 : 0;
    this.setSelectedSubAreaOptions(filters);
  }

  setSelectedSubAreaOptions(selectedSubAreaOptions) {
    this.selectedSubAreaOptions = selectedSubAreaOptions;
    /*
     * We want the subscore_keys part of metricResultsSearchObj to have the format
     * {
        "subscore_keys":[1,2,3]
       }
     *
     * */
    if (this.selectedSubAreaOptions && this.selectedSubAreaOptions.length > 0) {
      let keys = flatMap(this.selectedSubAreaOptions, (subAreaOption: any) => {
        return subAreaOption.subfield.keys;
      });
      this.metricResultsSearchObj = {
        ...this.metricResultsSearchObj,
        subscore_keys: keys,
      };
    } else {
      this.metricResultsSearchObj.subscore_keys = [];
    }
    //Only send a real-time calculator request if at least one strand is selected
    if (
      this.selectedSubArea == "Overall" ||
      (this.metricResultsSearchObj.subscore_keys &&
        this.metricResultsSearchObj.subscore_keys.length > 0)
    ) {
      this.handleRealtimeMetricCalculation();
    } else {
      this.clearAllChartData();
    }
  }

  unselectAllSubAreas() {
    this.setSelectedSubAreaOptions([]);
  }

  clearSubArea(item) {
    let field = item.field,
      subfield = item.subfield;
    // remove item from selectedSubAreaOptions and from the metricResultsSearchObj
    this.setSelectedSubAreaOptions(
      filter(this.selectedSubAreaOptions, (filterVal) => {
        return !(filterVal.field == field && filterVal.subfield == subfield);
      })
    );
  }

  selectCompareBy() {
    if (this.selectedCompareBy != this.noComparisonText) {
      this.metricResultsSearchObj = {
        ...this.metricResultsSearchObj,
        compare_by:
          this.selectedCompareBy == "School"
            ? "systemSchoolId"
            : this.selectedCompareBy,
      };
      this.kpiService
        .trackMetricZoomCompareBySelected(
          this.data.lightbox.kpiId,
          this.metricResultsSearchObj.compare_by
        )
        .subscribe();
    } else {
      this.metricResultsSearchObj = {
        ...this.metricResultsSearchObj,
        compare_by: null,
      };
    }
    this.handleRealtimeMetricCalculation();
  }

  mapCompareBy(compareByVal) {
    if (this.selectedCompareBy == "School") {
      var school = filter(this.historical_schools, [
        "systemSchoolId",
        compareByVal,
      ])[0];
      //Make sure we have the school to map to otherwise use school abbrev.
      //Can throw an error if schools.json isn't updated yet to match the dw where the metric calc is pulling from
      //Permissions are caught in the gateway and not returned
      if (school) {
        return school.officialSchoolName;
      } else {
        return compareByVal;
      }
    } else {
      return compareByVal;
    }
  }

  replacer(key, value) {
    if (value == null) {
      return "";
    }
    return value;
  }

  exportToCSV() {
    try {
      this.exportingData = true;
      this.kpiService
        .trackMetricZoomExportToCSVInitiated(this.data.lightbox.kpiId)
        .subscribe(
          (data) => {},
          (error) => {
            console.error(error);
          }
        );
      let fileName = this.getFileName();
      let headers = this.getHeaderRow();
      let headerRow = map(headers, (fieldName) => {
        return JSON.stringify(fieldName, this.replacer);
      }).join(",");

      let dataMaps = this.buildDataMap(this.selectedCompareBy == "School");
      let dataRows = map(dataMaps, (dataMap) => {
        return map(headers, (fieldName) => {
          return JSON.stringify(dataMap[fieldName], this.replacer);
        }).join(",");
      });

      let csv = [headerRow, ...dataRows];
      let url = window.URL.createObjectURL(
        new Blob([csv.join("\r\n")], { type: "text/csv" })
      );
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.href = url;
      a.download = fileName; // gives it a name via an a tag
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      this.exportingData = false;
      this.showExportCompletedMessage();
      this.kpiService
        .trackMetricZoomExportToCSVSuccess(this.data.lightbox.kpiId)
        .subscribe(
          (data) => {},
          (error) => {
            console.error(error);
          }
        );
    } catch (error) {
      console.error(error);
      this.exportingData = false;
      this.showExportFailedMessage();
    }
  }

  showExportCompletedMessage() {
    let successMessage = "Your CSV export is downloaded and ready for viewing!";
    this.snackBar.open(successMessage, "DISMISS", {
      duration: 3000,
    });
  }

  showExportFailedMessage() {
    let successMessage =
      "We're sorry.  An error occurred during your CSV export.";
    this.snackBar.open(successMessage, "DISMISS", {
      duration: 3000,
    });
  }

  getFileName() {
    let fileName = this.data.lightbox.title
      ? this.data.lightbox.title.replace(/\s/g, "")
      : "MetricZoom";
    let currentDate = formatDate(new Date(), "yyyy-MM-dd", "en");
    let strandName = this.contentText
      ? this.contentText.replace(/\s/g, "")
      : "Overall";
    let filterValues = this.filterByText
      ? "_" + this.filterByText.replace(/\s/g, "")
      : "";
    let compareByName =
      this.selectedCompareBy && this.selectedCompareBy != this.noComparisonText
        ? "_" + this.selectedCompareBy.replace(/\s/g, "")
        : "";
    fileName = encodeURIComponent(
      currentDate +
        "_" +
        fileName +
        "_" +
        strandName +
        filterValues +
        compareByName
    ).substring(0, 250);
    return fileName + ".csv";
  }

  getHeaderRow() {
    let columns = [];
    columns.push("Content");
    columns.push("Comparison Value");
    if (this.selectedCompareBy == "School") {
      columns.push("SystemSchoolID");
    }
    columns.push("Filters");
    if (this.selectedTimePeriods && this.selectedTimePeriods.length > 0) {
      forEach(this.selectedTimePeriods, (selectedTimePeriod) => {
        columns.push(selectedTimePeriod + " Numerator");
        columns.push(selectedTimePeriod + " Denominator");
        columns.push(selectedTimePeriod + " Value");
      });
    }
    return columns;
  }

  buildDataMap(mapSchoolNameToSystemSchoolId) {
    let filterValues = this.getFilterValues();
    let rows = [];
    forEach(this.filteredChartData, (series) => {
      if (series && series.data) {
        let datamap = {};
        datamap["Content"] = series.content;
        datamap["Comparison Value"] =
          series.compare_by ||
          (mapSchoolNameToSystemSchoolId
            ? this.getSystemSchoolName(series.stack)
            : series.stack);
        if (mapSchoolNameToSystemSchoolId) {
          datamap["SystemSchoolID"] =
            this.getSystemSchoolId(series.compare_by) || series.stack;
        }
        datamap["Filters"] = filterValues;
        forEach(series.data, (dataPoint) => {
          datamap[dataPoint.name + " Numerator"] = dataPoint.numerator;
          datamap[dataPoint.name + " Denominator"] = dataPoint.denominator;
          datamap[dataPoint.name + " Value"] = dataPoint.y;
        });
        rows.push(datamap);
      }
    });
    return rows;
  }

  getSystemSchoolId(schoolName) {
    let matchingSchools = filter(this.historical_schools, [
      "officialSchoolName",
      schoolName,
    ]);
    if (matchingSchools && matchingSchools.length > 0) {
      return matchingSchools[0].systemSchoolId;
    } else {
      return null;
    }
  }

  getSystemSchoolName(systemSchoolId) {
    let matchingSchools = filter(this.historical_schools, [
      "systemSchoolId",
      systemSchoolId,
    ]);
    if (matchingSchools && matchingSchools.length > 0) {
      return matchingSchools[0].officialSchoolName;
    } else {
      return null;
    }
  }

  getFilterValues() {
    if (
      this.metricResultsSearchObj &&
      this.metricResultsSearchObj.student_metric_filters
    ) {
      return map(
        this.metricResultsSearchObj.student_metric_filters,
        (filter) => {
          if (filter.field == "systemSchoolId") {
            return map(filter.values, (systemSchoolId) => {
              return this.getSystemSchoolName(systemSchoolId);
            });
          } else {
            return filter.values;
          }
        }
      ).join(", ");
    } else {
      return null;
    }
  }

  selectChart(chartType) {
    //Ignore clicks on the distribution chart icon if we don't show distribution charts for this metric
    if (
      chartType == "distribution" &&
      (!this.data.lightbox.charts.category ||
        !this.data.lightbox.charts.category.chartData)
    ) {
      return;
    }
    this.selectedChart = chartType;
    this.metricResultsSearchObj = {
      ...this.metricResultsSearchObj,
      chart_type: chartType,
    };
    if (chartType == "trend") {
      //Track trend clicks
      this.kpiService
        .trackMetricZoomTrendClicked(this.data.lightbox.kpiId)
        .subscribe(
          (data) => {},
          (error) => {
            console.error(error);
          }
        );
    } else if (chartType == "distribution") {
      //Track distribution clicks
      this.kpiService
        .trackMetricZoomDistributionClicked(this.data.lightbox.kpiId)
        .subscribe(
          (data) => {},
          (error) => {
            console.error(error);
          }
        );
    }
    if (
      chartType == "distribution" &&
      this.subAreaDropdown &&
      this.subAreaDropdown.length > 1
    ) {
      //Clear any selected sub areas
      this.selectedSubAreaOptions = [];
      this.subAreaOptions = [];
      this.selectedSubArea = "Overall";
      this.metricResultsSearchObj = {
        ...this.metricResultsSearchObj,
        subscore_content_area: this.selectedSubArea,
      };
      this.metricResultsSearchObj.subscore_keys = [];
    }
    this.handleRealtimeMetricCalculation();
  }

  updateChartData(chartData) {
    this.chartData = chartData;
  }

  hideMZInfoMessage() {
    localStorage.setItem("largeDatasetInfoMessageDist", "hidden");
  }

  isHiddenInLocalStorage() {
    return localStorage.getItem("largeDatasetInfoMessageDist") == "hidden";
  }

  hasLargeDataset() {
    return this.calculateNumBars() > DIST_CHART_LARGE_NUM_BARS;
  }

  calculateNumBars() {
    // get the max number of time periods which have data
    let numTimePeriods = max(
      map(this.filteredChartData, (series: Series) => {
        return series.data ? series.data.length : 0;
      })
    );
    // get the number of stacks (bars per time period)
    let numStacks =
      this.selectedCompareBy && this.selectedCompareBy != this.noComparisonText
        ? uniq(
            map(this.filteredChartData, (series: chartData) => {
              return series.stack;
            })
          ).length
        : 1;
    return numTimePeriods * numStacks;
  }

  showDistChartLabels() {
    return this.calculateNumBars() <= DIST_CHART_NUM_SHOW_LABELS;
  }

  customSort(data) {
    if (
      this.demographics &&
      (this.selectedCompareBy == "Current Grade Level" ||
        this.selectedCompareBy == "Grade Level At The Time")
    ) {
      let gradeLevelOrderArray = this.demographics["Current Grade Level"]
        ? this.demographics["Current Grade Level"]
        : this.demographics["Grade Level at the Time"];
      if (gradeLevelOrderArray && gradeLevelOrderArray.length > 0) {
        // check for Current Grade Level or Grade Level at the Time, sort by grade level numeric order instead of alphabetical
        data = sortBy(data, (item) => {
          return gradeLevelOrderArray.indexOf(item.stack);
        });
      }
    } else if (this.selectedCompareBy == "School") {
      data = sortBy(data, (item) => {
        return map(this.schools, "systemSchoolId").indexOf(item.stack);
      });
    }
    return data;
  }

  exportToPNG() {
    let metricZoomElement = document.getElementById("metricZoom");
    this.kpiService
      .trackMetricZoomExportToPNGInitiated(this.data.lightbox.kpiId)
      .subscribe(
        (data) => {},
        (error) => {
          console.error(error);
        }
      );
    html2canvas(metricZoomElement, {
      scale: 2,
    }).then((canvas) => {
      saveAs(
        canvas.toDataURL(),
        getDownloadTileName(this.data.lightbox.title, "metric-zoom")
      );
    });
  }
}
